import type { SupabaseClient } from '@supabase/supabase-js';
import { CloudError, type CloudApi, type DeleteResult, type PushInput, type PushResult, type RemoteDoc, type RemoteMeta } from './api';

/** The Supabase implementation of `CloudApi`: PostgREST reads under RLS, writes through the revision functions. */

interface PostgrestFailure {
  message?: string;
  code?: string;
  details?: string;
}

const PAGE = 500;
const META_COLUMNS = 'id, name, kind, revision, updated_at, deleted_at, workspace_id, sort_order, eol, has_bom';

/** Rejections that retrying can never fix: constraint, size and text-encoding violations. */
const REJECTED_CODES = new Set(['22P05', '22021', '22023', '22001', '23502', '23503', '23514', '54000', '54001']);

export function classifyFailure(error: PostgrestFailure, status: number): CloudError {
  const message = error.message ?? '';
  const code = error.code ?? '';
  if (status === 0) return new CloudError('network', 'The cloud could not be reached.');
  // An id that belongs to another account is a clash on this one document, not a verdict on the account.
  if (code === 'MT409') return new CloudError('rejected', 'This document’s identity belongs to another account. Make a copy of it to sync it here.');
  if (code === '42501' && /cloud access/i.test(message)) return new CloudError('denied', 'This account does not have access to MyTXT cloud storage.');
  if (status === 401 || code === '28000' || code === 'PGRST301' || code === 'PGRST303' || /jwt/i.test(message)) return new CloudError('auth', 'Your session has ended. Sign in again.');
  if (status === 403 || code === '42501') return new CloudError('denied', 'This account does not have access to MyTXT cloud storage.');
  if (REJECTED_CODES.has(code) || status === 413) return new CloudError('rejected', 'The cloud refused this document’s content.');
  if (status === 408 || status === 429 || status >= 500) return new CloudError('server', 'MyTXT cloud is busy. Retrying.');
  return new CloudError('rejected', 'The cloud could not accept this request.');
}

function unwrap<T>(result: { data: T | null; error: PostgrestFailure | null; status: number }): T | null {
  if (result.error) throw classifyFailure(result.error, result.status);
  return result.data;
}

/** Anything thrown by `fetch` itself (a lost connection) is a network failure. */
async function guard<T>(run: () => PromiseLike<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof CloudError) throw error;
    throw new CloudError('network', 'The cloud could not be reached.');
  }
}

export function createSupabaseApi(client: SupabaseClient): CloudApi {
  return {
    access: () =>
      guard(async () => {
        const data = unwrap(await client.rpc('has_cloud_access'));
        return data === true;
      }),

    list: () =>
      guard(async () => {
        // PostgREST caps a response (1000 rows by default): read the whole library page by page, in a stable order.
        const all: RemoteMeta[] = [];
        for (let from = 0; ; from += PAGE) {
          const page = unwrap(await client.from('documents').select(META_COLUMNS).order('updated_at', { ascending: false }).order('id').range(from, from + PAGE - 1)) ?? [];
          all.push(...(page as unknown as RemoteMeta[]));
          if (page.length < PAGE) return all;
        }
      }),

    fetch: (id) =>
      guard(async () => {
        const row = unwrap(await client.from('documents').select(`${META_COLUMNS}, content`).eq('id', id).maybeSingle());
        return (row ?? null) as unknown as RemoteDoc | null;
      }),

    push: (input: PushInput) =>
      guard(async () => {
        const data = unwrap(
          await client.rpc('push_document', {
            p_id: input.id,
            p_base_revision: input.baseRevision,
            p_name: input.name,
            p_kind: input.kind,
            p_content: input.content,
            p_eol: input.eol,
            p_has_bom: input.bom,
            p_workspace_id: input.workspace?.id ?? null,
            p_workspace_name: input.workspace?.name ?? null,
            p_sort_order: input.sortOrder,
          }),
        );
        return parseResult<PushResult>(data);
      }),

    remove: (id, baseRevision) =>
      guard(async () => {
        const data = unwrap(await client.rpc('delete_document', { p_id: id, p_base_revision: baseRevision }));
        return parseResult<DeleteResult>(data);
      }),
  };
}

function parseResult<T extends { status: string }>(data: unknown): T {
  if (!data || typeof data !== 'object' || typeof (data as { status?: unknown }).status !== 'string') {
    throw new CloudError('server', 'MyTXT cloud returned an unexpected answer.');
  }
  return data as T;
}
